import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { LexbeCase } from '../models/case.model';
import { EmailThreadGrouping } from '../models/email-thread-grouping.model';
import { EmailThreadSummary } from '../models/email-thread-summary.model';
import { PagingResponse } from '@models/paging-response.model';
import { ErrorHandlerService } from './error-handler.service';
import { UserConfigsService } from './user-configs.service';
import { RecoveryManager } from '../managers/recovery.manager';
import { OkCancelDialogComponent } from '../components/dialogs/ok-cancel/ok-cancel-dialog.component';
import { NotificationManager } from '../managers/notification.manager';

@Injectable({
	providedIn: 'root'
})
export class CasesService {
	private currentCase = new Subject<LexbeCase>();

	constructor(
		private http: HttpClient,
		private errorHandler: ErrorHandlerService,
		private appConfigService: AppConfigService,
		private userConfigService: UserConfigsService,
		private injector: Injector,
		private notificationManager: NotificationManager,
		private dialog: MatDialog
	) {

	}

	public setCurrentCase(caseId: string): Observable<any> {
		localStorage.removeItem('SearchItemList');
		localStorage.removeItem('SearchQuery');
		localStorage.removeItem('ContextMapping');

		const url = `${this.appConfigService.getConfig().webApi}/api2/UserCasePermissions/${caseId}`;
		return this.http.patch(url, {}).pipe(
			tap(() => this.userConfigService.refreshUserConfigs()),
			catchError(this.errorHandler.handleError<LexbeCase>(`setCurrentCase with caseId=${caseId}`)));
	}

	public addNewCase(myCase: LexbeCase): Observable<LexbeCase> {
		return this.http.post<LexbeCase>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases`, myCase);
	}

	public updateCase(caseId: string, deltaCase: Partial<LexbeCase>): Observable<void> {
		const body = [];
		for (const key in deltaCase) {
			body.push({
				op: "replace",
				path: `/${key}`,
				value: deltaCase[key]
			})
		}
		return this.http.patch<void>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases/${caseId}`, body)
			.pipe(catchError(this.errorHandler.handleError<void>(`[PATCH] Case failed for CaseId=${caseId}`)));
	}

	public deleteCaseDialog(caseId: string, callback: () => void): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Case`,
			message: `Delete Case? The case will be PERMANENTLY DELETED from the account. <br/> This action cannot be undone.`
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((okClicked: boolean) => {
			if (okClicked) {
				this.deleteCase(caseId).subscribe(() => {
					callback();
					this.notificationManager.displaySnackBarMessage({ message: 'Case deleted successfully.', ttl: 10000 });
				}, () => this.notificationManager.displaySnackBarMessage({ message: 'An Error has occurred. Please contact Professional Services for support.', type: 'error' }));
			}
		});
	}

	public deleteCase(caseId: string): Observable<void> {
		const recoveryManager = this.injector.get(RecoveryManager);
		return this.http.delete<void>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases/${caseId}`).pipe(
			catchError((error) => {
				return recoveryManager.handleError({
						message: `[DELETE] Case failed for CaseId=${caseId}, ${error.error.error.message}`,
						errorType: 'recoverable',
						internalError: error

					});
				}
			)
		);
	}

	public getAllCases(): Observable<PagingResponse<LexbeCase>> {
		return this.http.get<PagingResponse<LexbeCase>>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases`)
			.pipe(catchError(this.errorHandler.handleError<PagingResponse<LexbeCase>>(`getCases`)));
	}

	public getCases(pageIndex: number, pageSize: number, searchTerm?: string): Observable<PagingResponse<LexbeCase>> {
		return this.http.get<PagingResponse<LexbeCase>>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases`,
			{ params: this.setCaseQuery(pageIndex, pageSize, searchTerm) })
			.pipe(catchError(this.errorHandler.handleError<PagingResponse<LexbeCase>>(`getCases with searchTerm=${searchTerm}&pageIndex=${pageIndex}&pageSize=${pageSize}`)));
	}

	public getCase(id: string): Observable<LexbeCase> {
		return this.http.get<LexbeCase>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases/${id}`)
			.pipe(
				tap(data => LexbeCase.addComputedFields(data)),
				catchError(this.errorHandler.handleError<LexbeCase>(`getCase with id=${id}`)));
	}

	public showCurrentCase(currentCase: LexbeCase): void {
		this.currentCase.next(currentCase);
	}

	public listenCurrentCase(): Observable<LexbeCase> {
		return this.currentCase.asObservable();
	}

	public getEmailThreadGroupings(id: string, pageIndex: number, pageSize: number): Observable<EmailThreadGrouping[]> {
		return this.http.get<EmailThreadGrouping[]>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases/${id}/emailthreadgroupings`,
			{ params: this.setPagingParams(pageIndex, pageSize) })
			.pipe(catchError(this.errorHandler.handleError<EmailThreadGrouping[]>(`getEmailThreadGroupings with id=${id}`)));
	}

	public getEmailThreadSummary(id: string): Observable<EmailThreadSummary> {
		return this.http.get<EmailThreadSummary>(`${this.appConfigService.getConfig().webApi}/api2/lexbecases/${id}/emailthreadsummary`)
			.pipe(catchError(this.errorHandler.handleError<EmailThreadSummary>(`getEmailThreadSummary with id=${id}`)));
	}

	private setCaseQuery(pageIndex: number, pageSize: number, searchTerm: string): HttpParams {
		const params: HttpParams =
			this.setPagingParams(pageIndex, pageSize)
				.set('$count', 'true')
				.set('$orderby', 'caseName');
		if (!searchTerm)
			return params;

		return params.set('searchTerm',`${searchTerm.toLowerCase() }`);
	}

	private setPagingParams(pageIndex: number, pageSize: number): HttpParams {
		const skip = pageIndex * pageSize;
		return new HttpParams()
			.set('$skip', skip.toString())
			.set('$top', pageSize.toString());
	}
}
